import {throttle} from 'lodash'
import {IPoint, ISize, MusicPaperShape} from '@consts/types'
import soundMap from '@consts/soundMap';
import palettes from '@consts/palettes';
import MusicFactory from '@utils/MusicFactory'
import convertMouseEventToPosition from '../../utils/convertMouseEventToPosition';
import ResizeArea from './resize-area';
import eventEmitter, {Emitter} from './event-emitter';

type Events = {
  click: MusicPaperShape;
  moved: MusicPaperShape;
  edited: MusicPaperShape;
}

type MouseMoveHandler = (event: MouseEvent) => void;
type TouchMoveHandler = (event: TouchEvent) => void;

const hitOptions = {
	segments: true,
	stroke: true,
	fill: true,
	tolerance: 5
};

export abstract class AbstractFigure<
  CoreFigure extends paper.Path = paper.Path,
  ShapeData extends MusicPaperShape = MusicPaperShape
> {
  static readonly emitter: Emitter<Events> = eventEmitter<Events>()
  private readonly _coreFigure: CoreFigure;
  public isDisable: boolean = false;

  private _isPlayMode: boolean = false;
  private _isActive: boolean = false;
  private _ResizeArea: ResizeArea | null = null;
  private _lastMovePosition: IPoint | null = null;
  private _moveFigureHandler: MouseMoveHandler | null = null;
  private _touchFigureHandler: TouchMoveHandler | null = null;
  private _moveSegmentHandler: MouseMoveHandler | null = null;
  private _touchSegmentHandler: TouchMoveHandler | null = null;

  private _isScalable: boolean = false;

  protected abstract initCoreFigure(paperScope: paper.PaperScope, shape: ShapeData): CoreFigure;
  protected abstract updateCoreFigureByShapeData(
    coreFigure: CoreFigure,
    shape: ShapeData,
    paperScope: paper.PaperScope
  ): void;
  protected abstract serializationToShapeData(
    coreFigure: CoreFigure,
    shape: ShapeData,
  ): ShapeData;

  public onClick: VoidFunction | null = null;



  public constructor(
    private _paperScope: paper.PaperScope,
    private _shapeData: ShapeData
  ) {
    this._coreFigure = this.initCoreFigure(_paperScope, _shapeData);
    this.bindHandlers();
  }

  public updateFromShapeData(updateShapeData: ShapeData): void {
    this.updateCoreFigureByShapeData(this._coreFigure, updateShapeData, this._paperScope);
    this._shapeData = updateShapeData;
    this._ResizeArea?.updateSize(this._coreFigure.bounds);
  }

  public get shapeData() {
    return this._shapeData;
  }

  protected get static() {
    return AbstractFigure;
  }

  public get bounds() {
    return this._coreFigure.bounds;
  }

  public get id() {
    return this._shapeData.id;
  }

  public get isPlayMode() {
    return this._isPlayMode;
  }

  public set isPlayMode(status: boolean) {
    this._isPlayMode = status;
    this.isActive = false;
  }

  public get isActive() {
    return this._isActive;
  }

  public set isActive(state: boolean) {
    if (this._isActive === state) return;
    if (!this._ResizeArea) {
      this._ResizeArea = new ResizeArea(this._paperScope, this._coreFigure.bounds);
      this._ResizeArea.onResize = (size, position) => {
        this.resizeFigure(size, position);
        this._updateNotify();
      }
    }

    if (this.isCanChangePoints()) {
      this._coreFigure.selected = state;
    }
  
    this._ResizeArea.isShow = state;
    this._isActive = state;
  }

  public bringToFront() {
    this._coreFigure.bringToFront();
    this._ResizeArea?.bringToFront();
  }

  public remove() {
    if (this._moveFigureHandler) {
      window.removeEventListener('mousemove', this._moveFigureHandler)
    }
    if (this._touchFigureHandler) {
      window.removeEventListener('touchmove', this._touchFigureHandler);
    }

    if (this._moveSegmentHandler) {
      window.removeEventListener('mousemove', this._moveSegmentHandler);
    }

    if (this._touchSegmentHandler) {
      window.removeEventListener('touchmove', this._touchSegmentHandler);
    }

    if (this._ResizeArea) this._ResizeArea.remove();
    this._coreFigure.remove();
  }
  
  protected isSmoothFigure() {
    return false;
  }
  
  protected isCanChangePoints() {
    return false;
  }

  private playTrack() {
    const { track } = this._shapeData;
    if (!track) return;
    const { SoundPaletteKey, soundPosition } = track;
    const musicFactory = new MusicFactory(soundMap);
    const { matrix } = palettes[SoundPaletteKey];
    const musicRoad = soundPosition.map(({ x, y }) => {
      const { sound } = matrix[x][y];
      return sound;
    });

    musicFactory.playSong(musicRoad);
  }

  private _updateNotify = throttle(() => {
    if (this.isPlayMode) return;
    const updateShapeData = this.serializationToShapeData(this._coreFigure, this._shapeData);
    this._shapeData = updateShapeData;
    this.static.emitter.emit('edited', updateShapeData);
  }, 20)

  private bindHandlers() {
    this._coreFigure.strokeColor = new this._paperScope.Color(0, 0, 0, Number.EPSILON);
    this._coreFigure.selectedColor = new this._paperScope.Color(0, 1, 0);
    this._coreFigure.strokeWidth = 5;

    this.static.emitter.on('click', (shapeData) => {
      if (shapeData.id === this.id) return;
      this.isActive = false;
    })

    this._paperScope.project.view.on('mouseup', (event: paper.MouseEvent) => {
      if (this.isDisable || this._isPlayMode) return;
      this.unbindSegmentMoveHandler();

      const { shift: isShiftPress } = event.modifiers;
      if (!this.isActive || !isShiftPress) return;

      const hitResult = this._paperScope.project.hitTest(event.point, hitOptions);
      if (!hitResult) return;

      if (hitResult.item !== this._coreFigure || hitResult.type !== 'segment') return;
      hitResult.segment.remove();
      this._ResizeArea?.updateSize(this._coreFigure.bounds);
      event.stopPropagation();
    })

    this._coreFigure.onMouseEnter = () => {
      if (!this._isPlayMode) return;
      document.body.style.cursor = 'pointer';
    }

    this._coreFigure.onMouseLeave = () => {
      if (!this._isPlayMode) return;
      document.body.style.cursor = 'initial';
    }

    this._coreFigure.onMouseUp = () => {
      this.unbindFigureMoveHandler();
    }

    this._coreFigure.onMouseDown = (event: paper.MouseEvent) => {
      if (this._isPlayMode) {
        this.playTrack();
        if (!this._isScalable) {
          this._isScalable = true;
          this._coreFigure.scale(1.1);
          setTimeout(() => {
            this._coreFigure.scale(1/1.1);
            this._isScalable = false;
          }, 200);
        }
        return;
      }

      if (this.isDisable) {
        this.static.emitter.emit('click', this._shapeData);
        return;
      }
      let mustBindHandler = true;
      if (!this.isActive) {
        this._paperScope.project.activeLayer.selected = false;
        this.static.emitter.emit('click', this._shapeData);
        this.isActive = true;
      } else if (this.isCanChangePoints()) {
        const { project } = this._paperScope;
        const hitResult = project.hitTest(event.point, hitOptions);
        if (hitResult) {
          const { shift: isShiftPress } = event.modifiers;
          if (isShiftPress && hitResult.type === 'segment') {
            hitResult.segment.remove();
            this._ResizeArea?.updateSize(this._coreFigure.bounds);
            event.stopPropagation();
            mustBindHandler = false;
            this._updateNotify();
          } else if (hitResult.type === 'segment' && !isShiftPress) {
            this.bindSegmentMoveHandler(hitResult.segment);
            mustBindHandler = false;
          } else if (hitResult.type === 'stroke') {
            const position = hitResult.location.index + 1;
            const segment = this._coreFigure.insert(position, event.point)
            if (this.isSmoothFigure()) this._coreFigure.smooth();

            this._ResizeArea?.updateSize(this._coreFigure.bounds);
            this.bindSegmentMoveHandler(segment);
            mustBindHandler = false;
          }
        }
      }


      if (mustBindHandler) {
        const { point: { x, y } } = event;
        this.bindFigureMoveHandler({ x, y });
      }
      this.onClick && this.onClick();
    }
  }

  private bindSegmentMoveHandler(segment: paper.Segment) {
    this._moveSegmentHandler = throttle((event: MouseEvent) => {
      const position = convertMouseEventToPosition(event);
      segment.point.x = position.x;
      segment.point.y = position.y;
      this._ResizeArea?.updateSize(this._coreFigure.bounds);
    }, 20);
    this._touchSegmentHandler = throttle((event: TouchEvent) => {
      const position = convertMouseEventToPosition(event);
      segment.point.x = position.x;
      segment.point.y = position.y;
      this._ResizeArea?.updateSize(this._coreFigure.bounds);
    }, 20);

    window.addEventListener('mousemove', this._moveSegmentHandler);
    window.addEventListener('touchmove', this._touchSegmentHandler);
  }

  private unbindSegmentMoveHandler() {
    if (!this._moveSegmentHandler || !this._touchSegmentHandler) return;
    window.removeEventListener('mousemove', this._moveSegmentHandler);
    window.removeEventListener('touchmove', this._touchSegmentHandler);
    this._updateNotify()
    this._moveSegmentHandler = null;
    this._touchSegmentHandler = null;
  }

  // Move figure logic

  private bindFigureMoveHandler(position: IPoint) {
    this._lastMovePosition = position;
    this._moveFigureHandler = throttle((event) => this.moveFigure(event), 20)
    this._touchFigureHandler = throttle((event: TouchEvent) => this.moveFigure(event), 20)
    window.addEventListener('mousemove', this._moveFigureHandler);
    window.addEventListener('touchmove', this._touchFigureHandler);
  }

  private unbindFigureMoveHandler() {
    if (!this._moveFigureHandler || !this._touchFigureHandler) return;
    window.removeEventListener('mousemove', this._moveFigureHandler);
    window.removeEventListener('touchmove', this._touchFigureHandler);
    this._updateNotify();
    this._lastMovePosition = null;
  }

  private moveFigure(event: MouseEvent | TouchEvent) {
    if (!this._lastMovePosition) return;
    
    const updatePosition = convertMouseEventToPosition(event);
    const diffX = updatePosition.x - this._lastMovePosition.x;
    const diffY = updatePosition.y - this._lastMovePosition.y;
    this._coreFigure.position.x += diffX;
    this._coreFigure.position.y+= diffY;

    if (this._ResizeArea) this._ResizeArea.shift(diffX, diffY);
    this._lastMovePosition = updatePosition;
  }
  // resize logic

  get isResizing(): boolean {
    if (!this._ResizeArea) return false;
    return this._ResizeArea.isShow;
  }

  set isResizing(state: boolean) {
    if (!this._ResizeArea) {
      this._ResizeArea = new ResizeArea(this._paperScope, this._coreFigure.bounds);
      this._ResizeArea.onResize = (size, position) => {
        this.resizeFigure(size, position);
      }
    }
    this._ResizeArea.isShow = state;
  }

  private resizeFigure(size: ISize, position: IPoint) {
    this._coreFigure.bounds.width = size.width;
    this._coreFigure.bounds.height = size.height;
    this._coreFigure.bounds.x = position.x;
    this._coreFigure.bounds.y = position.y;
  }
}